import { mdiShareVariant } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import PPSwitch from 'components/PPSwitch'
import { Fragment, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { BRAND_FACEBOOK, FB_POST_TYPE_REEL, POST_TYPE_STORY } from 'shared/constants'
import { ComposerFacebookPost, FBAlbum } from 'shared/types'
import styles from '../BulkPost.pcss'
import FacebookAlbumsPicker from '../FacebookAlbumsPicker'
import { BulkNetworkOptionsProps } from './types'

const RESHARE_OPTIONS: PPSelectOptions = {
  new: { label: <FormattedMessage id="composer.labels.photos.new-post" /> },
  reshare: { label: <FormattedMessage id="composer.labels.photos.reshare" /> }
}

export function FacebookBulkOptions(props: BulkNetworkOptionsProps) {
  const { post, profiles, recycleSelector } = props
  const data = post.data as ComposerFacebookPost

  const selectedAlbums = (data.albums || []).reduce((map: any, album) => {
    map[album.ppid] = album
    return map
  }, {})

  const toggleReshare = (value: 'reshare' | 'new') => {
    const current = data.reshare
    if (current && value === 'new' || !current && value === 'reshare') {
      props.onUpdateFBReshare(post.id, !current)
    }
  }

  const applyReshareToAll = () => {
    props.onToggleFBReshareAll(Boolean(data.reshare))
  }

  const onSelectedAlbumsChange = (albums: Record<string, FBAlbum | undefined>, applyToAll: boolean) => {
    props.onSelectedFBAlbumsChange(post.id, albums, applyToAll)
  }

  const onPostTypeChange = (value: string) => {
    props.onPostTypeChange(post.id, BRAND_FACEBOOK, value)
  }

  const postTypeOptions = useMemo(() => {
    const options = [{
      value: 'post',
      label: 'Post'
    }]
    if (post.videoUrl) {
      options.push({
        value: FB_POST_TYPE_REEL,
        label: 'Reel'
      }, {
        value: POST_TYPE_STORY,
        label: 'Story'
      })
    } else if (post.imageUrl) {
      options.push({
        value: POST_TYPE_STORY,
        label: 'Story'
      })
    }
    return options
  }, [post.videoUrl, post.imageUrl])

  return (
    <Fragment>
      {post.content?.feed?.type === BRAND_FACEBOOK && (
        <PPSelect
          name="Post as"
          icon={<Icon path={mdiShareVariant} size="16px" />}
          options={RESHARE_OPTIONS}
          selectedValue={data.reshare ? 'reshare' : 'new'}
          className={styles.option}
          withCaret
          footerElement={(
            <Button className={styles['btn-dd-reshare']} onClick={applyReshareToAll}>
              <FormattedMessage id="general.labels.apply-to-all" />
            </Button>
          )}
          onSelectionChange={toggleReshare}
        />
      )}
      {post.imageUrl && (
        <div className={styles.option}>
          <FacebookAlbumsPicker
            profiles={profiles}
            post={{
              ...data,
              comments: data.comments || [],
              albums: data.albums || [],
              reshare: data.reshare,
              lastUsedVideoPostType: 'post'
            }}
            selectedAlbums={selectedAlbums}
            onSelectedAlbumsChange={onSelectedAlbumsChange}
          />
        </div>
      )}
      {postTypeOptions.length > 1 && (
        <div className={styles.option}>
          <div className={styles['option-box']}>
            <PPSwitch
              selectedValue={data.postType || 'post'}
              options={postTypeOptions}
              onSelectedValueChange={onPostTypeChange}
            />
          </div>
        </div>
      )}
      <div className={styles.option}>{recycleSelector}</div>
    </Fragment>
  )
}
